// ============================================================
// AsistenciaDiariaListPage — Listado diario de asistencia.
// Filtros: fecha, nivel, grado, sección, estado.
// Acciones: justificar, editar, exportar Excel.
// ============================================================

import React, { useState, useMemo, useEffect } from 'react';
import { I } from 'components/ui';
import {
  useAsistenciaDiariaPorFecha,
  useAccionesAsistenciaDiaria,
  todayLima,
} from 'hooks/useAsistenciaDiaria';
import {
  AsistenciaDiariaResponse,
  EstadoAsistenciaDiaria,
  ESTADOS_DIARIA,
  ESTADO_DIARIA_LABEL,
  ESTADO_DIARIA_COLOR,
  ESTADO_DIARIA_DOT,
} from 'types/asistenciaDiaria.types';

const NIVELES = ['INICIAL', 'PRIMARIA', 'SECUNDARIA'];

const Spinner: React.FC<{ size?: number }> = ({ size = 5 }) => (
  <svg className={`animate-spin w-${size} h-${size}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z" />
  </svg>
);

const AsistenciaDiariaListPage: React.FC = () => {
  const hoy = todayLima();
  const { fecha, setFecha, registros, loading, error, recargar, actualizarLocal } = useAsistenciaDiariaPorFecha(hoy);

  // Filtros adicionales (cliente)
  const [nivel, setNivel] = useState('');
  const [grado, setGrado] = useState('');
  const [seccion, setSeccion] = useState('');
  const [estado, setEstado] = useState<EstadoAsistenciaDiaria | ''>('');
  const [busqueda, setBusqueda] = useState('');

  // Modales
  const [modalJustificar, setModalJustificar] = useState<AsistenciaDiariaResponse | null>(null);
  const [modalEditar, setModalEditar] = useState<AsistenciaDiariaResponse | null>(null);

  // Catálogos derivados (de los registros del día)
  const grados = useMemo(() => {
    const set = new Set<string>();
    registros.forEach(r => { if (!nivel || r.nivel === nivel) set.add(r.gradoNombre); });
    return Array.from(set).sort();
  }, [registros, nivel]);

  const secciones = useMemo(() => {
    const set = new Set<string>();
    registros.forEach(r => {
      if ((!nivel || r.nivel === nivel) && (!grado || r.gradoNombre === grado)) {
        set.add(r.seccionNombre);
      }
    });
    return Array.from(set).sort();
  }, [registros, nivel, grado]);

  // Filtrado
  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return registros.filter(r => {
      if (nivel    && r.nivel !== nivel) return false;
      if (grado    && r.gradoNombre !== grado) return false;
      if (seccion  && r.seccionNombre !== seccion) return false;
      if (estado   && r.estado !== estado) return false;
      if (q) {
        const hay = `${r.estudianteNombres} ${r.estudianteApellidos} ${r.estudianteDni}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [registros, nivel, grado, seccion, estado, busqueda]);

  // Resumen del filtrado
  const resumen = useMemo(() => {
    const r = { total: filtrados.length, presente: 0, tardanza: 0, ausente: 0, justificado: 0 };
    for (const x of filtrados) {
      if (x.estado === 'PRESENTE')    r.presente++;
      else if (x.estado === 'TARDANZA')    r.tardanza++;
      else if (x.estado === 'AUSENTE')     r.ausente++;
      else if (x.estado === 'JUSTIFICADO') r.justificado++;
    }
    return r;
  }, [filtrados]);

  // Reset cascada de filtros
  useEffect(() => { setGrado(''); setSeccion(''); }, [nivel]);
  useEffect(() => { setSeccion(''); }, [grado]);

  // Validar fecha (no permitir futuro)
  const handleFechaChange = (val: string) => {
    if (val > hoy) return;
    setFecha(val);
  };

  const exportarExcel = () => {
    const filas = [
      ['Fecha', 'DNI', 'Estudiante', 'Nivel', 'Grado', 'Sección', 'Estado', 'Hora ingreso', 'Motivo', 'Registrado por'],
      ...filtrados.map(r => [
        r.fecha,
        r.estudianteDni,
        `${r.estudianteApellidos}, ${r.estudianteNombres}`,
        r.nivel,
        r.gradoNombre,
        r.seccionNombre,
        ESTADO_DIARIA_LABEL[r.estado],
        r.horaIngreso ?? '',
        r.motivo ?? '',
        r.registradoPorNombres ?? 'Sistema',
      ]),
    ];
    const csv = filas.map(f => f.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `asistencia-diaria-${fecha}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Asistencia diaria — Listado</h2>
        <p className="text-slate-400 text-sm mt-0.5">Consulta y corrige los registros de entrada al colegio</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Fecha</label>
            <input
              type="date"
              max={hoy}
              value={fecha}
              onChange={e => handleFechaChange(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Nivel</label>
            <select value={nivel} onChange={e => setNivel(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
              <option value="">Todos</option>
              {NIVELES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Grado</label>
            <select value={grado} onChange={e => setGrado(e.target.value)} disabled={grados.length === 0}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:opacity-50">
              <option value="">Todos</option>
              {grados.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Sección</label>
            <select value={seccion} onChange={e => setSeccion(e.target.value)} disabled={secciones.length === 0}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:opacity-50">
              <option value="">Todas</option>
              {secciones.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Estado</label>
            <select value={estado} onChange={e => setEstado(e.target.value as EstadoAsistenciaDiaria | '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
              <option value="">Todos</option>
              {ESTADOS_DIARIA.map(e => <option key={e} value={e}>{ESTADO_DIARIA_LABEL[e]}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1">Buscar</label>
            <input
              type="text"
              placeholder="Nombre o DNI…"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-100">
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold">
              Total: {resumen.total}
            </span>
            <span className="px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 font-bold">
              Presente: {resumen.presente}
            </span>
            <span className="px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">
              Tardanza: {resumen.tardanza}
            </span>
            <span className="px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">
              Ausente: {resumen.ausente}
            </span>
            <span className="px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold">
              Justificado: {resumen.justificado}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => recargar()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-xs font-semibold text-slate-700"
            >
              <I.Refresh size={14} /> Recargar
            </button>
            <button
              onClick={exportarExcel}
              disabled={filtrados.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50"
            >
              <I.Download size={14} /> Exportar
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600 text-sm">{error}</div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading && registros.length === 0 ? (
          <div className="flex justify-center py-16 text-blue-700"><Spinner size={8} /></div>
        ) : filtrados.length === 0 ? (
          <p className="text-slate-400 text-sm py-16 text-center">No hay registros con los filtros aplicados</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {['Estudiante', 'DNI', 'Nivel', 'Grado/Sec', 'Estado', 'Hora', 'Motivo', 'Registrado por', 'Acciones'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-[11px] font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtrados.map(r => (
                  <tr key={r.idAsistenciaDiaria} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-semibold text-slate-800 whitespace-nowrap">
                      {r.estudianteApellidos}, {r.estudianteNombres}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 tabular-nums">{r.estudianteDni}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{r.nivel}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{r.gradoNombre} {r.seccionNombre}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${ESTADO_DIARIA_COLOR[r.estado]}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${ESTADO_DIARIA_DOT[r.estado]}`} />
                        {ESTADO_DIARIA_LABEL[r.estado]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">
                      {r.horaIngreso ? r.horaIngreso.slice(0, 5) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 max-w-[200px] truncate" title={r.motivo ?? ''}>
                      {r.motivo || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {r.registradoPorNombres || <span className="text-slate-400">Sistema</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {(r.estado === 'AUSENTE' || r.estado === 'TARDANZA') && (
                          <button
                            onClick={() => setModalJustificar(r)}
                            className="px-2.5 py-1 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-bold whitespace-nowrap"
                          >
                            Justificar
                          </button>
                        )}
                        <button
                          onClick={() => setModalEditar(r)}
                          className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold inline-flex items-center gap-1"
                        >
                          <I.Edit size={12} /> Editar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modales */}
      {modalJustificar && (
        <ModalJustificar
          registro={modalJustificar}
          onClose={() => setModalJustificar(null)}
          onSaved={(reg) => { actualizarLocal(reg); setModalJustificar(null); }}
        />
      )}
      {modalEditar && (
        <ModalEditar
          registro={modalEditar}
          onClose={() => setModalEditar(null)}
          onSaved={(reg) => { actualizarLocal(reg); setModalEditar(null); }}
        />
      )}
    </div>
  );
};

// ── Modal: Justificar ─────────────────────────────────────────

const ModalJustificar: React.FC<{
  registro: AsistenciaDiariaResponse;
  onClose: () => void;
  onSaved: (r: AsistenciaDiariaResponse) => void;
}> = ({ registro, onClose, onSaved }) => {
  const [motivo, setMotivo] = useState(registro.motivo ?? '');
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const { justificar, submitting } = useAccionesAsistenciaDiaria();

  const enviar = async () => {
    const m = motivo.trim();
    if (!m) { setSubmitErr('El motivo es obligatorio.'); return; }
    if (m.length > 255) { setSubmitErr('Máximo 255 caracteres.'); return; }
    try {
      const reg = await justificar(registro.idAsistenciaDiaria, { motivo: m });
      onSaved(reg);
    } catch (err: unknown) {
      setSubmitErr(err instanceof Error ? err.message : 'Error al justificar');
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-head">
          <div>
            <h2>Justificar inasistencia</h2>
            <div className="sub">{registro.estudianteApellidos}, {registro.estudianteNombres} — {registro.fecha}</div>
          </div>
          <button onClick={onClose} className="sae-icon-btn"><I.X /></button>
        </div>
        <div className="modal-body grid gap-3">
          <div className="field">
            <label>Motivo <span className="required">*</span></label>
            <textarea
              value={motivo}
              onChange={e => { setMotivo(e.target.value); setSubmitErr(null); }}
              placeholder="Descripción del motivo (cita médica, enfermedad, etc.)"
              maxLength={255}
              rows={4}
              className="input"
            />
            <p className="text-xs text-slate-400 mt-1">{motivo.length} / 255</p>
          </div>
          {submitErr && (
            <div className="rounded-r-sm bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{submitErr}</div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button className="btn btn-primary" onClick={enviar} disabled={submitting}>
            {submitting ? 'Guardando…' : 'Justificar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modal: Editar ─────────────────────────────────────────────

const ModalEditar: React.FC<{
  registro: AsistenciaDiariaResponse;
  onClose: () => void;
  onSaved: (r: AsistenciaDiariaResponse) => void;
}> = ({ registro, onClose, onSaved }) => {
  const [estado, setEstado] = useState<EstadoAsistenciaDiaria>(registro.estado);
  const [hora, setHora] = useState(registro.horaIngreso ? registro.horaIngreso.slice(0, 5) : '');
  const [motivo, setMotivo] = useState(registro.motivo ?? '');
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const { actualizar, submitting } = useAccionesAsistenciaDiaria();

  const necesitaMotivo = estado === 'JUSTIFICADO';
  const necesitaHora   = estado === 'PRESENTE' || estado === 'TARDANZA';

  const enviar = async () => {
    if (necesitaMotivo && !motivo.trim()) { setSubmitErr('El motivo es obligatorio cuando el estado es Justificado.'); return; }
    try {
      const horaFinal = hora ? `${hora}:00` : undefined;
      const reg = await actualizar(registro.idAsistenciaDiaria, {
        estado,
        horaIngreso: necesitaHora ? horaFinal : undefined,
        motivo: necesitaMotivo ? motivo.trim() : null,
      });
      onSaved(reg);
    } catch (err: unknown) {
      setSubmitErr(err instanceof Error ? err.message : 'Error al actualizar');
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-head">
          <div>
            <h2>Editar asistencia</h2>
            <div className="sub">{registro.estudianteApellidos}, {registro.estudianteNombres} — {registro.fecha}</div>
          </div>
          <button onClick={onClose} className="sae-icon-btn"><I.X /></button>
        </div>
        <div className="modal-body grid gap-3">
          <div className="field">
            <label>Estado <span className="required">*</span></label>
            <select value={estado} onChange={e => setEstado(e.target.value as EstadoAsistenciaDiaria)} className="input">
              {ESTADOS_DIARIA.map(e => <option key={e} value={e}>{ESTADO_DIARIA_LABEL[e]}</option>)}
            </select>
          </div>
          {necesitaHora && (
            <div className="field">
              <label>Hora de ingreso</label>
              <input
                type="time"
                value={hora}
                onChange={e => setHora(e.target.value)}
                className="input"
              />
            </div>
          )}
          {necesitaMotivo && (
            <div className="field">
              <label>Motivo <span className="required">*</span></label>
              <textarea
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                maxLength={255}
                rows={3}
                className="input"
                placeholder="Justificación de la inasistencia"
              />
              <p className="text-xs text-slate-400 mt-1">{motivo.length} / 255</p>
            </div>
          )}
          {submitErr && (
            <div className="rounded-r-sm bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{submitErr}</div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button className="btn btn-primary" onClick={enviar} disabled={submitting}>
            {submitting ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AsistenciaDiariaListPage;
